/**
 * EditorBubbleDrag — Interactive drag handlers for dialogue bubble positioning
 * in the cinematic editor work area. Handles:
 *   - Drag bubble body to set step.bubblePos
 *   - Drag tail handle to set step.tailTip
 *
 * WYSIWYG viewport mapping:
 *   The work area is a scaled-down view of the full 1200x800 canvas.
 *   All positions are stored in canvas space (matching BubbleRenderer).
 *   The viewport container applies scale + offset so the editor preview
 *   matches playback exactly.
 *
 * Key design decisions:
 *   - Interactive zones (bubbleZone, tailHandle) are standalone scene objects
 *     (NOT added to a container) — Phaser containers can interfere with hit testing.
 *   - All visual elements go into a viewportContainer (scaled) → bubbleContainer
 *     so the entire bubble moves visually during drag without a full re-render.
 *   - Tail graphics are in a separate tailGfx so they can be redrawn mid-drag.
 *   - On pointerup, emits a non-suppressed STEP_UPDATED to trigger a clean re-render.
 *
 * Extracted from EditorLivePreview to keep file sizes manageable.
 */
import Phaser from 'phaser';
import { WORK_AREA, EditorEvents, CANVAS_W, CANVAS_H } from './EditorTypes';
import { EventBus } from '@/events/EventBus';
import {
  BUBBLE_TEMPLATES, drawBubbleBody, drawBubbleTail, computeTailOrigin,
  type BubbleTemplateConfig,
} from '../BubbleTemplates';
import type { DialogueStep } from '../CinematicTypes';
import { TECH } from '@/ui/TechUI';
import { t } from '@/i18n/I18n';

const EMOTION_COLORS: Record<string, number> = {
  alert: 0xff4444, calm: TECH.ACCENT, excited: TECH.SUCCESS,
};

const TAIL_HANDLE_R = 7;
const EMIT_INTERVAL = 80;

// ── Viewport mapping: work area = scaled-down WYSIWYG of full canvas ──
const VP_SCALE = Math.min(WORK_AREA.w / CANVAS_W, WORK_AREA.h / CANVAS_H);
const VP_W = CANVAS_W * VP_SCALE;
const VP_H = CANVAS_H * VP_SCALE;
const VP_OX = WORK_AREA.x + (WORK_AREA.w - VP_W) / 2;
const VP_OY = WORK_AREA.y + (WORK_AREA.h - VP_H) / 2;

/** Canvas coords → editor display coords */
function canvasToDisplay(cx: number, cy: number): { x: number; y: number } {
  return { x: cx * VP_SCALE + VP_OX, y: cy * VP_SCALE + VP_OY };
}

/** Editor display coords → canvas coords */
function displayToCanvas(dx: number, dy: number): { x: number; y: number } {
  return { x: (dx - VP_OX) / VP_SCALE, y: (dy - VP_OY) / VP_SCALE };
}

export class EditorBubbleDrag {
  private scene: Phaser.Scene;
  private parentContainer: Phaser.GameObjects.Container;

  // Viewport container (scaled) for WYSIWYG preview
  private viewportContainer: Phaser.GameObjects.Container | null = null;
  // Sub-container that holds all bubble visuals (moves as a unit during drag)
  private bubbleContainer: Phaser.GameObjects.Container | null = null;
  // Tail graphics (separate so we can redraw during tail drag)
  private tailGfx: Phaser.GameObjects.Graphics | null = null;

  // Drag state
  private bubbleDragActive = false;
  private tailDragActive = false;
  private lastX = 0;
  private lastY = 0;

  // Interactive objects — standalone scene objects (NOT in any container)
  private bubbleZone: Phaser.GameObjects.Rectangle | null = null;
  private tailHandle: Phaser.GameObjects.Arc | null = null;
  private moveHandler: ((p: Phaser.Input.Pointer) => void) | null = null;
  private upHandler: (() => void) | null = null;

  // Throttled emit (during drag to update step panel)
  private emitDirty = false;
  private emitTimer: ReturnType<typeof setTimeout> | null = null;
  private suppressUpdate = false;

  // Current step reference
  private step: DialogueStep | null = null;

  // Cached layout (canvas space — matches BubbleRenderer)
  private bubbleX = 0;
  private bubbleY = 0;
  private bubbleW = 0;
  private bubbleH = 0;
  private tmpl: BubbleTemplateConfig = BUBBLE_TEMPLATES.speech;
  private borderColor: number = TECH.ACCENT;
  private fillColor: number = TECH.BG;
  private textColor: number = 0xffffff;
  private hostSceneRef: Phaser.Scene | null = null;

  constructor(scene: Phaser.Scene, container: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.parentContainer = container;
  }

  /**
   * Render the BD-style bubble preview with interactive drag zones.
   * Called from EditorLivePreview.previewDialogue().
   */
  render(step: DialogueStep, hostScene: Phaser.Scene | null): void {
    this.cleanup();
    this.step = step;
    this.hostSceneRef = hostScene;

    // ── Resolve template + colors ──
    this.tmpl = BUBBLE_TEMPLATES[step.bubbleTemplate ?? 'trueBD'];
    const emotion = step.emotion ?? 'calm';
    this.borderColor = step.borderColor ?? EMOTION_COLORS[emotion] ?? this.tmpl.defaultBorderColor;
    this.fillColor = step.fillColor ?? this.tmpl.defaultFillColor;
    this.textColor = step.textColor ?? this.tmpl.defaultTextColor;

    const position = step.position ?? 'bottom';
    const hasPortrait = step.portrait ? this.scene.textures.exists(step.portrait) : false;
    const portraitMode = (step as any).portraitDisplay ?? 'inBubble';
    const inBubblePortrait = hasPortrait && portraitMode === 'inBubble';
    const standingSide = hasPortrait && portraitMode !== 'inBubble' ? portraitMode as string : null;

    // ── Bubble dimensions — must match BubbleRenderer exactly ──
    const BUBBLE_W = 380;
    const BUBBLE_PAD = 16;
    const portraitSize = 64;
    const portraitGap = 12;
    const textOffsetX = inBubblePortrait ? BUBBLE_PAD + portraitSize + portraitGap : BUBBLE_PAD;
    this.bubbleW = inBubblePortrait ? BUBBLE_W + portraitSize + portraitGap : BUBBLE_W;
    const textWrapW = this.bubbleW - textOffsetX - BUBBLE_PAD;

    // Measure text height dynamically (same as BubbleRenderer)
    const fullText = t(step.textKey);
    const measurer = this.scene.add.text(0, -9999, fullText, {
      fontSize: this.tmpl.fontSize, fontFamily: 'monospace', fontStyle: this.tmpl.fontStyle,
      wordWrap: { width: textWrapW }, lineSpacing: 4,
    });
    const textH = measurer.height;
    measurer.destroy();
    const minH = inBubblePortrait ? portraitSize + BUBBLE_PAD * 2 : 0;
    this.bubbleH = Math.max(BUBBLE_PAD + 24 + textH + BUBBLE_PAD, minH);

    // ── Bubble position in canvas space (same as BubbleRenderer) ──
    if (step.bubblePos) {
      this.bubbleX = step.bubblePos.x;
      this.bubbleY = step.bubblePos.y;
    } else {
      this.bubbleX = CANVAS_W / 2 - this.bubbleW / 2;
      if (position === 'top') this.bubbleY = 30;
      else if (position === 'center') this.bubbleY = (CANVAS_H - this.bubbleH) / 2;
      else this.bubbleY = CANVAS_H - this.bubbleH - 60;
    }

    this.drawBubble(step, inBubblePortrait, standingSide);
    this.setupDragHandlers();
  }

  /** Draw the complete bubble (body + content + tail + interactive zones) */
  private drawBubble(step: DialogueStep, inBubblePortrait: boolean, standingSide?: string | null): void {
    const { bubbleX: bx, bubbleY: by, bubbleW: bw, bubbleH: bh, tmpl } = this;
    const BUBBLE_PAD = 16;
    const portraitSize = 64;
    const portraitGap = 12;
    const textOffsetX = inBubblePortrait ? BUBBLE_PAD + portraitSize + portraitGap : BUBBLE_PAD;
    const textWrapW = bw - textOffsetX - BUBBLE_PAD;

    // ── Viewport container: scales full canvas into work area (WYSIWYG) ──
    this.viewportContainer = this.scene.add.container(VP_OX, VP_OY)
      .setScale(VP_SCALE).setDepth(5);
    this.parentContainer.add(this.viewportContainer);

    // ── Movable sub-container for bubble visuals (canvas space) ──
    this.bubbleContainer = this.scene.add.container(bx, by);
    this.viewportContainer.add(this.bubbleContainer);

    // Body (drawn at 0,0 inside sub-container)
    const bodyGfx = this.scene.add.graphics();
    drawBubbleBody(bodyGfx, 0, 0, bw, bh, tmpl,
      this.fillColor, tmpl.fillAlpha, this.borderColor, 1);
    this.bubbleContainer.add(bodyGfx);

    // Separator line under speaker name (matches BubbleRenderer)
    bodyGfx.lineStyle(1, this.borderColor, 0.4);
    bodyGfx.lineBetween(textOffsetX, BUBBLE_PAD + 20, bw - BUBBLE_PAD, BUBBLE_PAD + 20);

    // Portrait in bubble (same size/position as BubbleRenderer)
    if (inBubblePortrait && step.portrait) {
      const pImg = this.scene.add.image(
        BUBBLE_PAD + portraitSize / 2, bh / 2, step.portrait,
      ).setDisplaySize(portraitSize, portraitSize);
      const pGfx = this.scene.add.graphics();
      pGfx.lineStyle(1, this.borderColor, 0.5);
      pGfx.strokeRect(BUBBLE_PAD, bh / 2 - portraitSize / 2, portraitSize, portraitSize);
      this.bubbleContainer.add([pImg, pGfx]);
    }

    // Standing portrait (large, at screen edge — same coords as BubbleRenderer)
    if (standingSide && step.portrait && this.scene.textures.exists(step.portrait)) {
      const s = step as any;
      const scalePct = s.portraitScale ?? 0.4;
      const charOff = s.portraitOffset ?? { x: 0, y: 0 };
      const useFrame = s.portraitFrame && this.scene.textures.exists('frame_smartphone');
      const pc = this.scene.add.container(0, 0);

      // Fixed anchor — matches BubbleRenderer exactly (full canvas coords)
      const anchorX = standingSide === 'bottomLeft' ? 80 : CANVAS_W - 80;
      const anchorY = CANVAS_H - 20;
      pc.setPosition(anchorX, anchorY);

      // Phone frame (behind character, independent)
      if (useFrame) {
        const fScale = s.frameScale ?? 0.35;
        const fOff = s.frameOffset ?? { x: 0, y: 0 };
        const phone = this.scene.add.image(fOff.x, fOff.y, 'frame_smartphone');
        const phoneH = CANVAS_H * fScale;
        phone.setDisplaySize(phoneH * (phone.width / phone.height), phoneH);
        phone.setOrigin(0.5, 1);
        pc.add(phone);
      }

      // Character image (on top, independent)
      const charImg = this.scene.add.image(charOff.x, charOff.y, step.portrait);
      const charTargetH = CANVAS_H * scalePct;
      charImg.setDisplaySize(charTargetH * (charImg.width / charImg.height), charTargetH);
      charImg.setOrigin(0.5, 1);
      if ((step as any).portraitFlip) charImg.setFlipX(true);
      pc.add(charImg);

      this.viewportContainer.add(pc);
    }

    // Speaker name
    const borderHex = '#' + this.borderColor.toString(16).padStart(6, '0');
    const speaker = this.scene.add.text(textOffsetX, BUBBLE_PAD, t(step.speaker), {
      fontSize: tmpl.fontSize, color: borderHex,
      fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.bubbleContainer.add(speaker);

    // Dialogue text (full text, same wrap as BubbleRenderer)
    const textHex = '#' + this.textColor.toString(16).padStart(6, '0');
    const fullText = t(step.textKey);
    const txt = this.scene.add.text(textOffsetX, BUBBLE_PAD + 24, fullText, {
      fontSize: tmpl.fontSize, color: textHex,
      fontFamily: 'monospace', fontStyle: tmpl.fontStyle,
      wordWrap: { width: textWrapW }, lineSpacing: 4,
    });
    this.bubbleContainer.add(txt);

    // ── Tail ──
    this.drawTail(step);

    // ── Hint text (in parent container, not viewport — stays at editor UI position) ──
    const hint = this.scene.add.text(
      WORK_AREA.w / 2, WORK_AREA.y + 12,
      'Drag bubble to position \u00b7 Drag tail tip to aim',
      { fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace', align: 'center' },
    ).setOrigin(0.5, 0).setAlpha(0.6);
    this.parentContainer.add(hint);

    // ── Canvas boundary indicator (subtle dotted outline) ──
    const boundGfx = this.scene.add.graphics().setAlpha(0.2);
    boundGfx.lineStyle(1, 0xffffff, 0.4);
    boundGfx.strokeRect(VP_OX, VP_OY, VP_W, VP_H);
    this.parentContainer.add(boundGfx);

    // ── Bubble drag zone — standalone scene object in display space ──
    const dp = canvasToDisplay(bx + bw / 2, by + bh / 2);
    this.bubbleZone = this.scene.add.rectangle(
      dp.x, dp.y, bw * VP_SCALE, bh * VP_SCALE, 0x000000, 0,
    ).setDepth(8).setInteractive({ useHandCursor: true });
  }

  /** Draw (or redraw) tail graphics + tail handle */
  private drawTail(step: DialogueStep): void {
    // Clean previous tail
    if (this.tailGfx) { this.tailGfx.destroy(); this.tailGfx = null; }
    if (this.tailHandle) {
      this.tailHandle.removeAllListeners();
      this.tailHandle.destroy();
      this.tailHandle = null;
    }

    const tailTip = this.resolveTailTip(step);
    if (!tailTip || this.tmpl.tailStyle === 'none') return;

    const { bubbleX: bx, bubbleY: by, bubbleW: bw, bubbleH: bh } = this;

    // Tail drawn in canvas space inside viewport container
    this.tailGfx = this.scene.add.graphics();
    const origin = computeTailOrigin(bx, by, bw, bh, tailTip.x, tailTip.y);
    drawBubbleTail(this.tailGfx, origin.x, origin.y, tailTip.x, tailTip.y,
      this.tmpl, this.fillColor, this.tmpl.fillAlpha, this.borderColor, 1);
    if (this.viewportContainer) {
      this.viewportContainer.add(this.tailGfx);
    }

    // Tail handle — standalone scene object in display space
    const dtp = canvasToDisplay(tailTip.x, tailTip.y);
    this.tailHandle = this.scene.add.circle(dtp.x, dtp.y, TAIL_HANDLE_R, this.borderColor, 0.6)
      .setStrokeStyle(1.5, 0xffffff, 0.5)
      .setDepth(9)
      .setInteractive({ useHandCursor: true, cursor: 'crosshair' });

    // Re-attach pointerdown for tail drag
    this.tailHandle.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      this.tailDragActive = true;
      this.bubbleDragActive = false;
      this.lastX = ptr.x;
      this.lastY = ptr.y;
    });
  }

  /** Resolve tail tip position in canvas space, clamped to full canvas */
  private resolveTailTip(step: DialogueStep): { x: number; y: number } | null {
    let tip: { x: number; y: number } | null = null;

    if (step.tailTip) {
      tip = step.tailTip;
    } else if (step.worldAnchor && this.hostSceneRef) {
      const cam = this.hostSceneRef.cameras.main;
      const halfW = cam.width / 2;
      const halfH = cam.height / 2;
      tip = {
        x: halfW + (step.worldAnchor.x - cam.scrollX - halfW) * cam.zoom,
        y: halfH + (step.worldAnchor.y - cam.scrollY - halfH) * cam.zoom,
      };
    } else {
      // Default: below bubble center (gives the user a tail handle to drag)
      tip = {
        x: this.bubbleX + this.bubbleW / 2,
        y: this.bubbleY + this.bubbleH + 60,
      };
    }

    if (!tip) return null;

    // Clamp to full canvas (matching BubbleRenderer behavior)
    return {
      x: Phaser.Math.Clamp(tip.x, 0, CANVAS_W),
      y: Phaser.Math.Clamp(tip.y, 0, CANVAS_H),
    };
  }

  /** Setup pointerdown/move/up handlers */
  private setupDragHandlers(): void {
    // Bubble body drag
    if (this.bubbleZone) {
      this.bubbleZone.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
        this.bubbleDragActive = true;
        this.tailDragActive = false;
        this.lastX = ptr.x;
        this.lastY = ptr.y;
      });
    }

    // Tail handle pointerdown is set in drawTail()

    // Shared pointermove — updates position in real-time
    this.moveHandler = (ptr: Phaser.Input.Pointer) => {
      if (!ptr.isDown || !this.step) return;

      if (this.bubbleDragActive) {
        const dx = ptr.x - this.lastX;
        const dy = ptr.y - this.lastY;
        this.lastX = ptr.x;
        this.lastY = ptr.y;
        if (dx === 0 && dy === 0) return;

        // Convert display delta to canvas delta
        const cdx = dx / VP_SCALE;
        const cdy = dy / VP_SCALE;

        // Update position in canvas space, clamped to full canvas
        const nx = Phaser.Math.Clamp(this.bubbleX + cdx, 0, CANVAS_W - this.bubbleW);
        const ny = Phaser.Math.Clamp(this.bubbleY + cdy, 0, CANVAS_H - this.bubbleH);
        this.bubbleX = nx;
        this.bubbleY = ny;
        this.step.bubblePos = { x: Math.round(nx), y: Math.round(ny) };

        // Move the sub-container in canvas space (viewport handles display)
        if (this.bubbleContainer) this.bubbleContainer.setPosition(nx, ny);
        // Move the invisible drag zone in display space
        const dp = canvasToDisplay(nx + this.bubbleW / 2, ny + this.bubbleH / 2);
        if (this.bubbleZone) this.bubbleZone.setPosition(dp.x, dp.y);
        // Redraw tail from new bubble position
        this.drawTail(this.step);

        this.throttledEmit();
      }

      if (this.tailDragActive) {
        // Convert mouse display coords to canvas coords
        const canvasPos = displayToCanvas(ptr.x, ptr.y);
        const tx = Phaser.Math.Clamp(canvasPos.x, 0, CANVAS_W);
        const ty = Phaser.Math.Clamp(canvasPos.y, 0, CANVAS_H);
        this.step.tailTip = { x: Math.round(tx), y: Math.round(ty) };

        // Redraw tail to new tip position
        this.drawTail(this.step);

        this.throttledEmit();
      }
    };

    this.upHandler = () => {
      if (this.bubbleDragActive || this.tailDragActive) {
        this.bubbleDragActive = false;
        this.tailDragActive = false;
        this.flushEmit();
      }
    };

    this.scene.input.on('pointermove', this.moveHandler);
    this.scene.input.on('pointerup', this.upHandler);
  }

  // ── Throttled emit (suppressed to prevent re-render during drag) ──

  private throttledEmit(): void {
    this.emitDirty = true;
    if (this.emitTimer) return;
    this.emitTimer = setTimeout(() => {
      this.emitTimer = null;
      if (this.emitDirty) {
        this.emitDirty = false;
        this.emitSuppressed(EditorEvents.STEP_UPDATED);
      }
    }, EMIT_INTERVAL);
  }

  private flushEmit(): void {
    if (this.emitTimer) { clearTimeout(this.emitTimer); this.emitTimer = null; }
    if (this.emitDirty) {
      this.emitDirty = false;
      this.emitSuppressed(EditorEvents.STEP_UPDATED);
    }
  }

  private emitSuppressed(event: string): void {
    this.suppressUpdate = true;
    EventBus.emit(event);
    this.suppressUpdate = false;
  }

  get isSuppressing(): boolean { return this.suppressUpdate; }

  /** Remove all interactive handlers, zones, and sub-container */
  cleanup(): void {
    if (this.emitTimer) { clearTimeout(this.emitTimer); this.emitTimer = null; }
    if (this.emitDirty) {
      this.emitDirty = false;
      this.emitSuppressed(EditorEvents.STEP_UPDATED);
    }
    if (this.moveHandler) {
      this.scene.input.off('pointermove', this.moveHandler);
      this.moveHandler = null;
    }
    if (this.upHandler) {
      this.scene.input.off('pointerup', this.upHandler);
      this.upHandler = null;
    }
    // Standalone scene objects — must destroy manually (not in any container)
    if (this.bubbleZone) { this.bubbleZone.destroy(); this.bubbleZone = null; }
    if (this.tailHandle) { this.tailHandle.destroy(); this.tailHandle = null; }
    if (this.tailGfx) { this.tailGfx.destroy(); this.tailGfx = null; }
    // viewportContainer + bubbleContainer are inside parentContainer —
    // they get destroyed by container.removeAll(true) but we null the refs
    this.viewportContainer = null;
    this.bubbleContainer = null;
    this.bubbleDragActive = false;
    this.tailDragActive = false;
    this.step = null;
  }

  destroy(): void {
    this.cleanup();
  }
}
